/**
 * Il sentiment di giornata (SPEC-026 §8): le due letture del giorno — borsa e
 * crypto — il confronto e le impostazioni per ambito.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { AssetDomain } from '@/lib/asset-domain';

export type SentimentDirection = 'risk_on' | 'risk_off' | 'neutral' | 'mixed';

export type SentimentReading = {
  id: string;
  domain: AssetDomain;
  readingDate: string;
  revision: number;
  status: 'queued' | 'running' | 'completed' | 'failed';
  trigger: string;
  direction: SentimentDirection | null;
  score: number | null;
  confidence: 'bassa' | 'media' | 'alta' | null;
  indicators: Array<{ name: string; value: string; detail: string | null; asOf: string; source: string }>;
  notMeasured: string[];
  drivers: Array<{ text: string; sourceUrl: string | null }>;
  sources: Array<{ url: string; title: string | null }>;
  summary: string | null;
  llmModel: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
};

export type SentimentSettings = { domain: AssetDomain; enabled: boolean; runAtLocalTime: string; timezone: string };

export type SentimentToday = {
  readingDate: string;
  /** La riga di oggi, in qualunque stato. */
  readings: Record<AssetDomain, SentimentReading | null>;
  /**
   * L'ultima lettura completata, di qualunque giorno (F-178): è l'esito che
   * la card mostra finché quella di oggi non c'è. Facoltativo per un backend
   * non ancora aggiornato.
   */
  latest?: Record<AssetDomain, SentimentReading | null>;
  comparison: { divergence: 'aligned' | 'opposed' | 'unclear'; note: string };
  settings: Record<AssetDomain, SentimentSettings>;
};

/** Un indicatore reso numero (F-177): `value` è il numero, `display` com'era scritto. */
export type SentimentFigure = { name: string; value: string; display: string };

export type SentimentHistoryPoint = {
  date: string;
  status: 'completed' | 'failed';
  direction: SentimentDirection | null;
  score: number | null;
  confidence: 'bassa' | 'media' | 'alta' | null;
  revision: number;
  revisionCount: number;
  completedAt: string | null;
  figures: SentimentFigure[];
};

export type SentimentDivergence = 'aligned' | 'opposed' | 'unclear';

export type SentimentTimeline = {
  from: string;
  to: string;
  stock: SentimentHistoryPoint[];
  crypto: SentimentHistoryPoint[];
  comparisons: Array<{ date: string; divergence: SentimentDivergence }>;
};

export const SENTIMENT_HISTORY_WINDOWS = [7, 30, 90] as const;

const TODAY_KEY = ['market-sentiment', 'today'];

export function useSentimentToday() {
  return useQuery({
    queryKey: TODAY_KEY,
    queryFn: () => api.get<SentimentToday>('/market-sentiment/today'),
    // Una lettura in corso dura un minuto o due: la card si aggiorna da sola.
    refetchInterval: (query) => {
      const readings = query.state.data?.readings;
      const busy = readings && Object.values(readings).some((r) => r?.status === 'queued' || r?.status === 'running');
      return busy ? 10_000 : false;
    },
  });
}

/** Lo storico compatto dei due ambiti (F-177): esito e numeri per giorno. */
export function useSentimentTimeline(days: number) {
  return useQuery({
    queryKey: ['market-sentiment', 'timeline', days],
    queryFn: () => api.get<SentimentTimeline>(`/market-sentiment/timeline?days=${days}`),
  });
}

export function useRunSentiment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domain: AssetDomain) => api.post<{ readingId: string; created: boolean }>(`/market-sentiment/run?domain=${domain}`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: TODAY_KEY }),
  });
}

export function useUpdateSentimentSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ domain, ...input }: Partial<SentimentSettings> & { domain: AssetDomain }) =>
      api.put<SentimentSettings>(`/market-sentiment/settings?domain=${domain}`, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: TODAY_KEY }),
  });
}
